import type {
  AssignmentStatus,
  FormFillSession,
  FormSubmissionDetail,
  FormSubmissionDocument,
  ResolvedForm,
} from '@/features/forms/api/types';

type SubmissionDetailWire = FormSubmissionDetail & {
  resolvedForm?: ResolvedForm;
  submission?: FormSubmissionDetail;
  formName?: string;
  assignmentStatus?: AssignmentStatus;
};

function pickId(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function pickDocument(
  ...values: Array<FormSubmissionDocument | null | undefined>
): FormSubmissionDocument | null | undefined {
  for (const value of values) {
    if (!value) continue;
    if (value.pdf_download_url || value.url) return value;
  }
  return values.find((value) => value != null);
}

function resolveAssignmentStatus(
  raw: SubmissionDetailWire,
  nested: FormSubmissionDetail | undefined,
): AssignmentStatus | undefined {
  return (
    raw.assignment_status ??
    raw.assignmentStatus ??
    raw.assignment?.status ??
    nested?.assignment_status ??
    nested?.assignment?.status
  );
}

function resolveFormName(
  raw: SubmissionDetailWire,
  nested: FormSubmissionDetail | undefined,
  fallbackName?: string,
): string {
  return (
    raw.form_name ??
    raw.formName ??
    raw.form?.name ??
    nested?.form_name ??
    nested?.form?.name ??
    fallbackName ??
    'Form'
  );
}

/** Staff can edit until admin close-and-sync (assignment approved) or terminal states. */
export function isStaffSubmissionEditable(status?: AssignmentStatus): boolean {
  return status !== 'approved' && status !== 'cancelled' && status !== 'expired';
}

export function normalizeSubmissionDetail(raw: SubmissionDetailWire): FormSubmissionDetail {
  const nested = raw.submission;
  const resolvedForm = raw.resolved_form ?? raw.resolvedForm ?? nested?.resolved_form;
  const form = raw.form ?? nested?.form;
  const patient = raw.patient ?? nested?.patient;
  const assignment = raw.assignment ?? nested?.assignment;
  const assignmentId = pickId(raw.assignment_id, nested?.assignment_id, assignment?.id);
  const assignmentStatus = resolveAssignmentStatus(raw, nested);
  const formId = pickId(raw.form_id, nested?.form_id, form?.id);
  const formName = resolveFormName(raw, nested);
  const patientId = raw.patient_id ?? nested?.patient_id ?? patient?.id;
  const document = pickDocument(raw.document, nested?.document);
  const documentUrl =
    raw.document_url ??
    nested?.document_url ??
    document?.pdf_download_url ??
    document?.url ??
    null;

  const normalized: FormSubmissionDetail = {
    status: raw.status ?? nested?.status ?? 'submitted',
    version: raw.version ?? nested?.version ?? 0,
    payload: raw.payload ?? nested?.payload ?? {},
    signature_file_key: raw.signature_file_key ?? nested?.signature_file_key ?? null,
    ...(raw.signature_url ?? nested?.signature_url
      ? { signature_url: raw.signature_url ?? nested?.signature_url ?? null }
      : {}),
    ...(raw.media ?? nested?.media ? { media: raw.media ?? nested?.media! } : {}),
    ...(pickId(raw.id, raw.submission_id, nested?.id, nested?.submission_id)
      ? {
          id: pickId(raw.id, raw.submission_id, nested?.id, nested?.submission_id)!,
          submission_id: pickId(raw.submission_id, raw.id, nested?.submission_id, nested?.id)!,
        }
      : {}),
    ...(formId ? { form_id: formId } : {}),
    ...(formName ? { form_name: formName } : {}),
    ...(assignmentId ? { assignment_id: assignmentId } : {}),
    ...(assignmentStatus ? { assignment_status: assignmentStatus } : {}),
    ...(patientId != null ? { patient_id: patientId } : {}),
    ...(form ? { form: { ...form, name: form.name || formName } } : formId ? { form: { id: formId, name: formName, version: raw.version ?? nested?.version ?? 0 } } : {}),
    ...(patient ? { patient } : patientId != null ? { patient: { id: patientId, first_name: '' } } : {}),
    ...(assignment
      ? { assignment: { ...assignment, ...(assignmentStatus ? { status: assignmentStatus } : {}) } }
      : assignmentId && assignmentStatus
        ? { assignment: { id: assignmentId, status: assignmentStatus } }
        : {}),
    ...(resolvedForm ? { resolved_form: resolvedForm } : {}),
    ...(raw.submitted_at ?? nested?.submitted_at ? { submitted_at: raw.submitted_at ?? nested?.submitted_at ?? null } : {}),
    ...(raw.approved_at ?? nested?.approved_at ? { approved_at: raw.approved_at ?? nested?.approved_at ?? null } : {}),
    ...(raw.rejected_at ?? nested?.rejected_at ? { rejected_at: raw.rejected_at ?? nested?.rejected_at ?? null } : {}),
    ...(document ? { document } : {}),
    ...(documentUrl ? { document_url: documentUrl } : {}),
    ...(raw.submission_hash ?? nested?.submission_hash ? { submission_hash: raw.submission_hash ?? nested?.submission_hash! } : {}),
    ...(raw.consent_extracted ?? nested?.consent_extracted
      ? { consent_extracted: raw.consent_extracted ?? nested?.consent_extracted! }
      : {}),
    ...(raw.consent_fanout_status ?? nested?.consent_fanout_status
      ? { consent_fanout_status: raw.consent_fanout_status ?? nested?.consent_fanout_status! }
      : {}),
    ...(raw.consent_fanout_error !== undefined || nested?.consent_fanout_error !== undefined
      ? { consent_fanout_error: raw.consent_fanout_error ?? nested?.consent_fanout_error ?? null }
      : {}),
    ...(raw.latest_elva_write_status !== undefined || nested?.latest_elva_write_status !== undefined
      ? { latest_elva_write_status: raw.latest_elva_write_status ?? nested?.latest_elva_write_status ?? null }
      : {}),
    ...(raw.elva_write_summary !== undefined || nested?.elva_write_summary !== undefined
      ? { elva_write_summary: raw.elva_write_summary ?? nested?.elva_write_summary ?? null }
      : {}),
    ...(raw.sync_status ?? nested?.sync_status
      ? { sync_status: raw.sync_status ?? nested?.sync_status! }
      : {}),
    ...(raw.write_plan ?? nested?.write_plan
      ? { write_plan: raw.write_plan ?? nested?.write_plan! }
      : {}),
    ...(raw.write_plan_version ?? nested?.write_plan_version
      ? { write_plan_version: raw.write_plan_version ?? nested?.write_plan_version! }
      : {}),
    ...(raw.signature_keys ?? nested?.signature_keys
      ? { signature_keys: raw.signature_keys ?? nested?.signature_keys! }
      : {}),
  };

  return normalized;
}

export function adaptSubmissionToSession(
  detail: SubmissionDetailWire,
  fallbackName?: string,
): FormFillSession | null {
  const normalized = normalizeSubmissionDetail(detail);
  if (!normalized.resolved_form) return null;

  const submissionId = normalized.id ?? normalized.submission_id ?? '';
  const formName = resolveFormName(normalized, undefined, fallbackName);
  const assignmentStatus = resolveAssignmentStatus(normalized, undefined) ?? 'submitted';

  return {
    assignment: normalized.assignment ?? {
      id: normalized.assignment_id ?? '',
      status: assignmentStatus,
    },
    form: normalized.form ?? {
      id: normalized.form_id ?? '',
      name: formName,
      version: normalized.version ?? 0,
    },
    patient: normalized.patient ?? {
      id: normalized.patient_id ?? 0,
      first_name: '',
    },
    resolved_form: normalized.resolved_form,
    submission: {
      ...normalized,
      id: submissionId,
      submission_id: submissionId,
    },
    editable: isStaffSubmissionEditable(assignmentStatus),
  };
}
